import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, Plus, DollarSign, CheckCircle, Edit2, Trash2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const BudgetPlanner = ({ user }) => {
  const navigate = useNavigate();
  const [budgetItems, setBudgetItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddItem, setShowAddItem] = useState(false);
  const [totalBudget, setTotalBudget] = useState(0);
  const [showSetBudget, setShowSetBudget] = useState(false);
  const [tempBudget, setTempBudget] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({
    category: 'venue',
    item_name: '',
    estimated_cost: '',
    actual_cost: '',
    vendor_name: ''
  });

  useEffect(() => {
    loadBudget();
    loadTotalBudget();
  }, [user.id]);

  const loadBudget = async () => {
    try {
      const response = await axios.get(`${API}/budget-items/${user.id}`);
      setBudgetItems(response.data);
    } catch (error) {
      console.error('Error loading budget:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTotalBudget = async () => {
    try {
      const response = await axios.get(`${API}/wedding-plans/${user.id}`);
      if (response.data?.total_budget) {
        setTotalBudget(response.data.total_budget);
      }
    } catch (error) {
      console.error('Error loading total budget:', error);
    }
  };

  const handleSetTotalBudget = async () => {
    const budgetValue = parseFloat(tempBudget.replace(/[^0-9.]/g, ''));
    if (isNaN(budgetValue) || budgetValue <= 0) {
      toast.error('Please enter a valid budget amount');
      return;
    }
    try {
      await axios.put(`${API}/wedding-plans/${user.id}`, {
        total_budget: budgetValue
      });
      setTotalBudget(budgetValue);
      setShowSetBudget(false);
      setTempBudget('');
      toast.success('Budget set successfully!');
    } catch (error) {
      toast.error('Failed to set budget');
    }
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/budget-items/${editingItem.id}`, {
        ...editingItem,
        estimated_cost: parseFloat(editingItem.estimated_cost) || 0,
        actual_cost: editingItem.actual_cost ? parseFloat(editingItem.actual_cost) : null
      });
      toast.success('Budget item updated!');
      setEditingItem(null);
      loadBudget();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/budget-items/${itemId}`);
      toast.success('Item deleted');
      loadBudget();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/budget-items`, {
        ...newItem,
        estimated_cost: parseFloat(newItem.estimated_cost),
        actual_cost: newItem.actual_cost ? parseFloat(newItem.actual_cost) : null
      }, {
        headers: { 'user-id': user.id }
      });
      toast.success('Budget item added!');
      setNewItem({ category: 'venue', item_name: '', estimated_cost: '', actual_cost: '', vendor_name: '' });
      setShowAddItem(false);
      loadBudget();
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleMarkPaid = async (itemId) => {
    try {
      await axios.put(`${API}/budget-items/${itemId}/paid`);
      toast.success('Marked as paid');
      loadBudget();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const totalEstimated = budgetItems.reduce((sum, item) => sum + item.estimated_cost, 0);
  const totalSpent = budgetItems.reduce((sum, item) => sum + (item.actual_cost || 0), 0);
  const totalPaid = budgetItems.filter(item => item.paid).reduce((sum, item) => sum + (item.actual_cost || item.estimated_cost), 0);

  const categories = {
    venue: 'Venue',
    catering: 'Catering',
    photography: 'Photography',
    videography: 'Videography',
    flowers: 'Flowers & Decor',
    music: 'Music & Entertainment',
    attire: 'Attire',
    invitations: 'Invitations',
    transportation: 'Transportation',
    other: 'Other'
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]">
      <div className="bg-white/70 backdrop-blur-xl border-b border-stone-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Button
            data-testid="back-to-dashboard-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#57534E] hover:text-[#881337]"
          >
            <ArrowLeft className="mr-2" size={18} />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="text-[#881337]" size={24} fill="#881337" />
            <span className="text-xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl font-serif font-light tracking-tight text-[#1C1917] mb-2">
              Budget Planner
            </h1>
            <p className="text-[#57534E]">Track expenses and stay within budget</p>
          </div>
          <Button
            data-testid="add-budget-item-btn"
            onClick={() => setShowAddItem(!showAddItem)}
            className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white"
          >
            <Plus className="mr-2" size={18} />
            Add Item
          </Button>
        </div>

        {/* Set Total Budget Section */}
        <div className="bg-gradient-to-r from-[#881337] to-[#9F1239] rounded-2xl p-6 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-rose-200 text-sm mb-1">Your Total Wedding Budget</p>
              {totalBudget > 0 ? (
                <p className="text-4xl font-serif">${totalBudget.toLocaleString()}</p>
              ) : (
                <p className="text-2xl font-serif text-rose-200">Not set yet</p>
              )}
            </div>
            {showSetBudget ? (
              <div className="flex items-center gap-3">
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                  <Input
                    type="text"
                    value={tempBudget}
                    onChange={(e) => setTempBudget(e.target.value)}
                    placeholder="25,000"
                    className="pl-10 bg-white text-stone-800 rounded-full w-40"
                    autoFocus
                  />
                </div>
                <Button
                  onClick={handleSetTotalBudget}
                  className="rounded-full bg-white text-[#881337] hover:bg-rose-100"
                >
                  Save
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { setShowSetBudget(false); setTempBudget(''); }}
                  className="rounded-full text-white hover:bg-white/20"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => setShowSetBudget(true)}
                className="rounded-full bg-white text-[#881337] hover:bg-rose-100"
              >
                <DollarSign className="mr-2" size={18} />
                {totalBudget > 0 ? 'Update Budget' : 'Set Your Budget'}
              </Button>
            )}
          </div>
          {totalBudget > 0 && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-rose-200 mb-2">
                <span>Allocated: ${totalEstimated.toLocaleString()}</span>
                <span>Available: ${(totalBudget - totalEstimated).toLocaleString()}</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${totalEstimated > totalBudget ? 'bg-red-400' : 'bg-white'}`}
                  style={{ width: `${Math.min(100, (totalEstimated / totalBudget) * 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Budget Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div data-testid="budget-summary-estimated" className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/50">
            <p className="text-sm text-[#A8A29E] mb-2">Allocated Budget</p>
            <p className="text-3xl font-serif text-[#1C1917]">${totalEstimated.toLocaleString()}</p>
          </div>
          <div data-testid="budget-summary-spent" className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/50">
            <p className="text-sm text-[#A8A29E] mb-2">Total Spent</p>
            <p className="text-3xl font-serif text-[#881337]">${totalSpent.toLocaleString()}</p>
          </div>
          <div data-testid="budget-summary-remaining" className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/50">
            <p className="text-sm text-[#A8A29E] mb-2">Remaining</p>
            <p className={`text-3xl font-serif ${totalEstimated - totalSpent >= 0 ? 'text-[#84A98C]' : 'text-red-500'}`}>
              ${(totalEstimated - totalSpent).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Add Item Form */}
        {showAddItem && (
          <form onSubmit={handleAddItem} className="bg-white/70 backdrop-blur-xl rounded-2xl p-8 border border-white/50 mb-8 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[#1C1917] mb-2 block font-medium">Category</label>
                <Select value={newItem.category} onValueChange={(value) => setNewItem({ ...newItem, category: value })}>
                  <SelectTrigger data-testid="budget-category-select" className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categories).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[#1C1917] mb-2 block font-medium">Item Name</label>
                <Input
                  data-testid="budget-item-name-input"
                  value={newItem.item_name}
                  onChange={(e) => setNewItem({ ...newItem, item_name: e.target.value })}
                  className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-[#1C1917] mb-2 block font-medium">Estimated Cost</label>
                <Input
                  data-testid="budget-estimated-input"
                  type="number"
                  step="0.01"
                  value={newItem.estimated_cost}
                  onChange={(e) => setNewItem({ ...newItem, estimated_cost: e.target.value })}
                  className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0"
                  required
                />
              </div>
              <div>
                <label className="text-[#1C1917] mb-2 block font-medium">Actual Cost</label>
                <Input
                  data-testid="budget-actual-input"
                  type="number"
                  step="0.01"
                  value={newItem.actual_cost}
                  onChange={(e) => setNewItem({ ...newItem, actual_cost: e.target.value })}
                  className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0"
                />
              </div>
              <div>
                <label className="text-[#1C1917] mb-2 block font-medium">Vendor</label>
                <Input
                  data-testid="budget-vendor-input"
                  value={newItem.vendor_name}
                  onChange={(e) => setNewItem({ ...newItem, vendor_name: e.target.value })}
                  className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button data-testid="save-budget-item-btn" type="submit" className="flex-1 rounded-full bg-[#881337] hover:bg-[#9F1239] text-white">
                Save Item
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowAddItem(false)} className="rounded-full">
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Budget Items */}
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-[#1C1917]">Category</th>
                  <th className="text-left p-4 text-sm font-medium text-[#1C1917]">Item</th>
                  <th className="text-left p-4 text-sm font-medium text-[#1C1917]">Vendor</th>
                  <th className="text-right p-4 text-sm font-medium text-[#1C1917]">Estimated</th>
                  <th className="text-right p-4 text-sm font-medium text-[#1C1917]">Actual</th>
                  <th className="text-center p-4 text-sm font-medium text-[#1C1917]">Status</th>
                  <th className="text-center p-4 text-sm font-medium text-[#1C1917]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {budgetItems.map((item) => (
                  <tr key={item.id} data-testid={`budget-item-${item.id}`} className="border-b border-stone-100 hover:bg-stone-50/50 transition-colors">
                    <td className="p-4 text-sm text-[#57534E]">{categories[item.category]}</td>
                    <td className="p-4 text-sm font-medium text-[#1C1917]">{item.item_name}</td>
                    <td className="p-4 text-sm text-[#57534E]">{item.vendor_name || '—'}</td>
                    <td className="p-4 text-sm text-right text-[#1C1917]">${item.estimated_cost.toLocaleString()}</td>
                    <td className="p-4 text-sm text-right text-[#881337]">
                      {item.actual_cost ? `$${item.actual_cost.toLocaleString()}` : '—'}
                    </td>
                    <td className="p-4 text-center">
                      <button
                        data-testid={`mark-paid-${item.id}-btn`}
                        onClick={() => !item.paid && handleMarkPaid(item.id)}
                        disabled={item.paid}
                        className="inline-flex items-center gap-2 text-sm"
                      >
                        <CheckCircle 
                          className={item.paid ? 'text-[#84A98C]' : 'text-stone-300 hover:text-[#84A98C]'} 
                          size={20}
                          fill={item.paid ? '#84A98C' : 'none'}
                        />
                        <span className={item.paid ? 'text-[#84A98C]' : 'text-[#57534E]'}>
                          {item.paid ? 'Paid' : 'Unpaid'}
                        </span>
                      </button>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          data-testid={`edit-budget-${item.id}-btn`}
                          onClick={() => setEditingItem({ ...item })}
                          className="p-2 text-stone-400 hover:text-[#881337] transition-colors"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          data-testid={`delete-budget-${item.id}-btn`}
                          onClick={() => handleDeleteItem(item.id)}
                          className="p-2 text-stone-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {budgetItems.length === 0 && (
            <div className="text-center py-16">
              <DollarSign className="mx-auto text-stone-300 mb-4" size={64} />
              <p className="text-[#57534E] mb-6">No budget items yet. Start tracking your expenses!</p>
              <Button
                onClick={() => setShowAddItem(true)}
                className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white"
              >
                <Plus className="mr-2" size={18} />
                Add Your First Item
              </Button>
            </div>
          )}
        </div>

        {/* Edit Item Modal */}
        {editingItem && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-[#881337] to-[#9F1239] px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <Edit2 className="text-white" size={20} />
                    </div>
                    <h3 className="font-serif font-medium text-white">Edit Budget Item</h3>
                  </div>
                  <button onClick={() => setEditingItem(null)} className="text-white/80 hover:text-white">
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleUpdateItem} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#1C1917] mb-2 block text-sm font-medium">Category</label>
                    <Select value={editingItem.category} onValueChange={(value) => setEditingItem({ ...editingItem, category: value })}>
                      <SelectTrigger className="rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(categories).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[#1C1917] mb-2 block text-sm font-medium">Item Name</label>
                    <Input
                      value={editingItem.item_name}
                      onChange={(e) => setEditingItem({ ...editingItem, item_name: e.target.value })}
                      className="rounded-lg"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-[#1C1917] mb-2 block text-sm font-medium">Estimated Cost</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingItem.estimated_cost}
                      onChange={(e) => setEditingItem({ ...editingItem, estimated_cost: e.target.value })}
                      className="rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-[#1C1917] mb-2 block text-sm font-medium">Actual Cost</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingItem.actual_cost || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, actual_cost: e.target.value })}
                      className="rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-[#1C1917] mb-2 block text-sm font-medium">Vendor</label>
                    <Input
                      value={editingItem.vendor_name || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, vendor_name: e.target.value })}
                      className="rounded-lg"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 rounded-full bg-[#881337] hover:bg-[#9F1239] text-white">
                    Save Changes
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setEditingItem(null)} className="rounded-full">
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetPlanner;